/**
 * Shared geographic drawing utilities used by GeoFence and PoiManager.
 * All coordinate systems follow GeoJSON convention: [lng, lat].
 * Leaflet positions follow: [lat, lng].
 */
import L from 'leaflet';

export const DRAW_MODE = {
  NONE: 'none',
  POLYGON: 'polygon',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
};

export const CATEGORY_ICONS = {
  runway: '✈',
  taxiway: '↔',
  apron: '🛬',
  terminal: '🏢',
  hangar: '🏭',
  parking_area: '🅿',
  maintenance_area: '🛠',
  road: '🛣',
  building: '🏗',
  bay_area: '⚓',
  gate: '🚪',
  restricted_zone: '⛔',
  other: '📍',
};

export const CATEGORY_COLORS = {
  runway: '#1d1d1d',
  taxiway: '#faad14',
  apron: '#8c8c8c',
  terminal: '#1890ff',
  hangar: '#722ed1',
  parking_area: '#52c41a',
  maintenance_area: '#d89614',
  road: '#fa8c16',
  building: '#eb2f96',
  bay_area: '#13c2c2',
  gate: '#2f54eb',
  restricted_zone: '#f5222d',
  other: '#6f6f6f',
};

export const CATEGORY_LABELS = {
  runway: 'Runway',
  taxiway: 'Taxiway',
  apron: 'Apron',
  terminal: 'Terminal',
  hangar: 'Hangar',
  parking_area: 'Parking Area',
  maintenance_area: 'Maintenance Area',
  road: 'Road',
  building: 'Building',
  bay_area: 'Bay Area',
  gate: 'Gate',
  restricted_zone: 'Restricted Zone',
  other: 'Other',
};

export const DEFAULT_STYLE = {
  color: '#6f6f6f',
  fillColor: '#6f6f6f',
  fillOpacity: 0.2,
  weight: 1,
};

// ── Coordinate helpers ──────────────────────────────────────────────────────

export function toGeoJsonPoint(latLng) {
  return [Number(latLng[1]), Number(latLng[0])];
}

export function closeRing(ring) {
  if (!ring || !ring.length) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return ring;
  return ring.concat([first]);
}

export function polygonFromLatLngs(latLngs) {
  if (!latLngs || latLngs.length < 3) return null;
  const ring = closeRing(latLngs.map(toGeoJsonPoint));
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} };
}

export function rectangleLatLngs(start, end) {
  if (!start || !end) return [];
  const minLat = Math.min(start[0], end[0]);
  const maxLat = Math.max(start[0], end[0]);
  const minLng = Math.min(start[1], end[1]);
  const maxLng = Math.max(start[1], end[1]);
  return [[minLat, minLng], [minLat, maxLng], [maxLat, maxLng], [maxLat, minLng]];
}

export function destinationPoint(center, bearingDegrees, distanceMeters) {
  const earthRadius = 6378137;
  const bearing = (bearingDegrees * Math.PI) / 180;
  const lat1 = (center[0] * Math.PI) / 180;
  const lng1 = (center[1] * Math.PI) / 180;
  const angularDistance = distanceMeters / earthRadius;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
    Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing),
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2),
  );
  return [lat2 * 180 / Math.PI, lng2 * 180 / Math.PI];
}

export function circlePolygonFeature(center, radiusMeters, vertices) {
  if (!center || !radiusMeters || radiusMeters <= 0) return null;
  const points = [];
  const total = vertices || 64;
  for (let i = 0; i < total; i += 1) {
    points.push(destinationPoint(center, (360 * i) / total, radiusMeters));
  }
  return polygonFromLatLngs(points);
}

export function isCoordinatePair(value) {
  return Array.isArray(value) && value.length >= 2 &&
    Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]));
}

export function toLatLng(value) {
  if (!isCoordinatePair(value)) return null;
  return [Number(value[1]), Number(value[0])];
}

export function areLatLngEqual(a, b) {
  return !!a && !!b && Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1]);
}

export function normalizeLatLngRing(latLngs) {
  const normalized = Array.isArray(latLngs)
    ? latLngs.filter(isCoordinatePair).map((item) => [Number(item[0]), Number(item[1])])
    : [];
  if (normalized.length > 1 && areLatLngEqual(normalized[0], normalized[normalized.length - 1])) {
    return normalized.slice(0, normalized.length - 1);
  }
  return normalized;
}

export function getPrimaryPolygonRingLatLngs(feature) {
  const geometry = feature && feature.geometry;
  if (!geometry || !geometry.type) return [];
  if (geometry.type === 'Polygon') {
    const ring = Array.isArray(geometry.coordinates) && geometry.coordinates.length
      ? geometry.coordinates[0] : [];
    return normalizeLatLngRing((ring || []).map(toLatLng).filter(Boolean));
  }
  if (geometry.type === 'MultiPolygon') {
    const first = Array.isArray(geometry.coordinates) && geometry.coordinates.length
      ? geometry.coordinates[0] : [];
    const ring = Array.isArray(first) && first.length ? first[0] : [];
    return normalizeLatLngRing((ring || []).map(toLatLng).filter(Boolean));
  }
  return [];
}

// ── Measurement helpers ─────────────────────────────────────────────────────

export function calculateDistanceMeters(from, to) {
  if (!from || !to) return 0;
  return L.latLng(Number(from[0]), Number(from[1])).distanceTo(L.latLng(Number(to[0]), Number(to[1])));
}

export function calculateSegmentDistancesMeters(latLngs, closePath = true) {
  const points = normalizeLatLngRing(latLngs);
  if (points.length < 2) return [];
  const distances = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    distances.push(calculateDistanceMeters(points[i], points[i + 1]));
  }
  if (closePath && points.length > 2) {
    distances.push(calculateDistanceMeters(points[points.length - 1], points[0]));
  }
  return distances.filter((v) => Number.isFinite(v) && v >= 0);
}

export function calculateGeodesicAreaSqm(latLngs) {
  const points = normalizeLatLngRing(latLngs);
  if (points.length < 3) return 0;
  const d2r = Math.PI / 180;
  const R = 6378137;
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const cur = points[i];
    const nxt = points[(i + 1) % points.length];
    area += ((nxt[1] - cur[1]) * d2r) * (2 + Math.sin(cur[0] * d2r) + Math.sin(nxt[0] * d2r));
  }
  return Math.abs((area * R * R) / 2);
}

export function sumNumbers(values) {
  return (values || []).reduce((s, v) => s + Number(v || 0), 0);
}

export function formatMetricNumber(value, maxFractionDigits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: maxFractionDigits });
}

export function formatDistanceMeters(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const d = n >= 100 ? 0 : 1;
  return `${formatMetricNumber(n, d)} m`;
}

export function formatAreaSquareMeters(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const d = n >= 1000 ? 0 : 2;
  return `${formatMetricNumber(n, d)} sq m`;
}

export function resolveShapeType(feature, shapeType) {
  if (typeof shapeType === 'string' && shapeType.trim()) return shapeType.trim().toLowerCase();
  const g = feature && feature.geometry && feature.geometry.type;
  if (g === 'Point' || g === 'MultiPoint') return 'point';
  if (g === 'LineString' || g === 'MultiLineString') return 'line';
  return DRAW_MODE.POLYGON;
}

export function computeShapeMetrics(feature, shapeType, options = {}) {
  if (!feature || !feature.geometry) return null;
  const st = resolveShapeType(feature, shapeType);
  const ringLatLngs = getPrimaryPolygonRingLatLngs(feature);

  if (st === DRAW_MODE.CIRCLE) {
    const areaSqm = ringLatLngs.length >= 3 ? calculateGeodesicAreaSqm(ringLatLngs) : null;
    const rOverride = Number(options.radiusOverrideMeters);
    const radiusMeters = Number.isFinite(rOverride) && rOverride > 0
      ? rOverride
      : (Number.isFinite(Number(areaSqm)) && Number(areaSqm) > 0 ? Math.sqrt(Number(areaSqm) / Math.PI) : null);
    return {
      areaSqm,
      radiusMeters,
      perimeterMeters: Number.isFinite(radiusMeters) ? 2 * Math.PI * radiusMeters : null,
      segmentDistancesMeters: [],
    };
  }

  if (st === DRAW_MODE.POLYGON || st === DRAW_MODE.RECTANGLE) {
    if (!ringLatLngs.length) return null;
    const seg = calculateSegmentDistancesMeters(ringLatLngs, true);
    return {
      areaSqm: ringLatLngs.length >= 3 ? calculateGeodesicAreaSqm(ringLatLngs) : null,
      radiusMeters: null,
      perimeterMeters: seg.length ? sumNumbers(seg) : null,
      segmentDistancesMeters: seg,
    };
  }
  return null;
}

export function getMetricDetailLines(metrics, shapeType, options = {}) {
  if (!metrics) return [];
  const lines = [];
  if (Number.isFinite(Number(metrics.areaSqm))) lines.push(`Area: ${formatAreaSquareMeters(metrics.areaSqm)}`);
  if (shapeType === DRAW_MODE.CIRCLE && Number.isFinite(Number(metrics.radiusMeters))) {
    lines.push(`Radius: ${formatDistanceMeters(metrics.radiusMeters)}`);
  }
  if ((shapeType === DRAW_MODE.POLYGON || shapeType === DRAW_MODE.RECTANGLE) && Array.isArray(metrics.segmentDistancesMeters)) {
    const prefix = shapeType === DRAW_MODE.RECTANGLE ? 'Side' : 'Edge';
    metrics.segmentDistancesMeters.forEach((d, i) => lines.push(`${prefix} ${i + 1}: ${formatDistanceMeters(d)}`));
  }
  if (Number.isFinite(Number(metrics.perimeterMeters)) && shapeType !== DRAW_MODE.CIRCLE) {
    lines.push(`Perimeter: ${formatDistanceMeters(metrics.perimeterMeters)}`);
  }
  if (options.includeCircumference && shapeType === DRAW_MODE.CIRCLE && Number.isFinite(Number(metrics.perimeterMeters))) {
    lines.push(`Circumference: ${formatDistanceMeters(metrics.perimeterMeters)}`);
  }
  return lines;
}

export function getMetricPrimarySummary(metrics, shapeType) {
  if (!metrics) return '';
  const parts = [];
  if (Number.isFinite(Number(metrics.areaSqm))) parts.push(`Area: ${formatAreaSquareMeters(metrics.areaSqm)}`);
  if (shapeType === DRAW_MODE.CIRCLE && Number.isFinite(Number(metrics.radiusMeters))) {
    parts.push(`Radius: ${formatDistanceMeters(metrics.radiusMeters)}`);
  }
  if ((shapeType === DRAW_MODE.POLYGON || shapeType === DRAW_MODE.RECTANGLE) && Number.isFinite(Number(metrics.perimeterMeters))) {
    parts.push(`Perimeter: ${formatDistanceMeters(metrics.perimeterMeters)}`);
  }
  return parts.join(' | ');
}

// ── Feature style helpers ───────────────────────────────────────────────────

export function getFeatureStyle(feature, fallbackStyle) {
  const fs = feature && feature.properties && feature.properties.style;
  return { ...DEFAULT_STYLE, ...(fallbackStyle || {}), ...(fs || {}) };
}

export function getInteractiveLayerStyle(feature, fallbackStyle, options = {}) {
  const base = getFeatureStyle(feature, fallbackStyle);
  const selected = !!options.selected;
  const pending = !!options.pending;
  return {
    ...base,
    dashArray: pending ? '6 4' : undefined,
    weight: selected ? Math.max(Number(base.weight) || DEFAULT_STYLE.weight, 2) + 1 : base.weight,
    fillOpacity: selected ? Math.min((Number(base.fillOpacity) || DEFAULT_STYLE.fillOpacity) + 0.12, 0.85) : base.fillOpacity,
  };
}

// ── HTML helpers ────────────────────────────────────────────────────────────

export function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function buildShapeTooltipHtml(label, metrics, shapeType) {
  const safeLabel = escapeHtml(String(label || ''));
  const detailLines = getMetricDetailLines(metrics, shapeType, { includeCircumference: true });
  const metricHtml = detailLines.map((l) => `<div class="geo-fence-tooltip-row">${escapeHtml(l)}</div>`).join('');
  return [
    '<div class="geo-fence-tooltip-content">',
    safeLabel ? `<div class="geo-fence-tooltip-title">${safeLabel}</div>` : '',
    metricHtml,
    '</div>',
  ].join('');
}

// ── Misc ────────────────────────────────────────────────────────────────────

export function parseLatLngInput(text) {
  const value = String(text || '').trim();
  if (!value) return null;
  const parts = value.split(/[,\s]+/).filter(Boolean);
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lat, lng];
}

export function createLocalId(prefix) {
  return `${prefix || 'shape'}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
}

export function stopLeafletEventPropagation(event) {
  if (event && event.originalEvent && typeof event.originalEvent.stopPropagation === 'function') {
    event.originalEvent.stopPropagation();
  }
}

export function extractErrorMessage(error) {
  const msg = error && error.response && error.response.data && error.response.data.message;
  if (Array.isArray(msg)) return msg[0];
  if (typeof msg === 'string' && msg) return msg;
  return null;
}

export function getMidPointLatLng(start, end) {
  if (!start || !end) return null;
  return [(Number(start[0]) + Number(end[0])) / 2, (Number(start[1]) + Number(end[1])) / 2];
}

export function buildFenceFeatureCollection(fences = [], idField = '_id') {
  const features = (fences || [])
    .map((fence) => {
      const feature = fence && fence.feature;
      const fenceId = fence && fence[idField];
      if (!feature || feature.type !== 'Feature' || !fenceId) return null;
      return { ...feature, properties: { ...(feature.properties || {}), _fenceId: String(fenceId) } };
    })
    .filter(Boolean);
  if (!features.length) return null;
  return { type: 'FeatureCollection', features };
}

export function getUserOrganisationMeta(user) {
  const organisation = user && user.organisation;
  if (!organisation) return { id: null, name: '' };
  if (typeof organisation === 'string') return { id: String(organisation), name: '' };
  const id = organisation._id || organisation.id || null;
  const name = organisation.name || organisation.label || '';
  return { id: id ? String(id) : null, name: name ? String(name) : '' };
}

export function flattenRoleAccess(roles = []) {
  return (roles || []).reduce((acc, role) => acc.concat((role && role.access) || []), []);
}

export function getResourcePermissions(roles = [], resourceName = '') {
  const accessEntries = flattenRoleAccess(roles);
  const permissionSet = new Set();
  accessEntries.forEach((entry) => {
    if (!entry || entry.name !== resourceName || !Array.isArray(entry.permissions)) return;
    entry.permissions.forEach((p) => permissionSet.add(String(p)));
  });
  return Array.from(permissionSet);
}

// ── Self-intersection detection ──────────────────────────────────────────────

function ccw(ax, ay, bx, by, cx, cy) {
  return (cy - ay) * (bx - ax) > (by - ay) * (cx - ax);
}

function segmentsIntersect(ax, ay, bx, by, cx, cy, dx, dy) {
  return (
    ccw(ax, ay, cx, cy, dx, dy) !== ccw(bx, by, cx, cy, dx, dy) &&
    ccw(ax, ay, bx, by, cx, cy) !== ccw(ax, ay, bx, by, dx, dy)
  );
}

/**
 * Returns true if the closed polygon defined by latLngs has any self-intersecting edges.
 * latLngs: [[lat, lng], ...] (open ring — first point NOT repeated at end).
 */
export function polygonHasSelfIntersections(latLngs) {
  const pts = normalizeLatLngRing(latLngs);
  const n = pts.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i += 1) {
    const i1 = (i + 1) % n;
    const [ax, ay] = pts[i];
    const [bx, by] = pts[i1];
    for (let j = i + 2; j < n; j += 1) {
      // Skip adjacent edges (share a vertex)
      if (i === 0 && j === n - 1) continue;
      const j1 = (j + 1) % n;
      const [cx, cy] = pts[j];
      const [dx, dy] = pts[j1];
      if (segmentsIntersect(ax, ay, bx, by, cx, cy, dx, dy)) return true;
    }
  }
  return false;
}

export function getUserFromLocalStorage() {
  try {
    const user = window.localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (e) {
    return null;
  }
}
